import type { ConductorRowResponse, EarthingResponse } from "../services/earthing";
import { formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";

// The stored runs the conductor rows of an earthing study read their figures from (EOS-08a,
// A36, D3 and D5), in the shared UX-1 contracts beside the SLD link panel: one line per linked
// row naming the run, its revision and what was read - a board conductor's earth-fault current
// from an EOS-04 run, a circuit protective conductor's t, I and S from a row of an EOS-05d run.
// The figures themselves are the result's (the tables print them); the links are pinned per
// row in the run's references by the backend. Nothing is shown for a study whose rows named no
// run (the panel is not rendered at all).

/** "EC-01: earth-fault current 35.22 kA from fault run SC-MSB-01-EFX rev 1 (Maximum · Single-phase to earth at bus MSB-01)" */
export function faultRunLinkText(row: ConductorRowResponse): string | null {
  const run = row.linked_fault_run;
  if (run === null) {
    return null;
  }
  const words = `${(CASE_LABELS as Record<string, string>)[run.calculation_case] ?? run.calculation_case} · ${
    (FAULT_TYPE_LABELS as Record<string, string>)[run.fault_type] ?? run.fault_type
  }`;
  return (
    `${row.code}: earth-fault current ${formatQuantity(run.earth_fault_current_ka, "kA").display} ` +
    `from fault run ${run.study_code} rev ${run.revision_number} (${words} at bus ${run.fault_bus_code})`
  );
}

/** "PE-C-01: t, I and S from earth-fault loop run EFL-REF-01 rev 1, row C-01 (disconnection assured)" */
export function disconnectionRowLinkText(row: ConductorRowResponse): string | null {
  const link = row.disconnection_row;
  if (link === null) {
    return null;
  }
  const assured =
    link.disconnection_assured === null
      ? "disconnection not compared"
      : link.disconnection_assured
        ? "disconnection assured"
        : "disconnection not assured";
  return (
    `${row.code}: t, I and S from earth-fault loop run ${link.study_code} rev ${link.revision_number}, ` +
    `row ${link.row_code} (${assured})`
  );
}

type EarthingLinkPanelProps = {
  result: EarthingResponse;
};

export function EarthingLinkPanel({ result }: EarthingLinkPanelProps) {
  const linked = result.conductors.filter((row) => row.linked_fault_run !== null || row.disconnection_row !== null);
  if (linked.length === 0) {
    return null;
  }
  const reviewed = linked.filter((row) => row.status === "REVIEW_REQUIRED").length;
  return (
    <section aria-label="Linked runs" data-summary-tone={reviewed === 0 ? "pass" : "warning"}>
      <header>
        <p data-summary-status={reviewed === 0 ? "VALID" : "REVIEW_REQUIRED"}>
          <StatusIcon tone={reviewed === 0 ? "pass" : "review"} size={20} />
          {linked.length === 1 ? "1 conductor row reads a stored run" : `${linked.length} conductor rows read stored runs`}
        </p>
        <p>
          {reviewed === 0
            ? "Every linked figure was read from the frozen result of its run; a declared figure governs where one was typed."
            : `${reviewed} linked row(s) carry a review item: see the row's warnings.`}
        </p>
      </header>
      <ul data-link-count={linked.length}>
        {linked.map((row) => {
          const fault = faultRunLinkText(row);
          const disconnection = disconnectionRowLinkText(row);
          return (
            <li key={row.code} data-linked-row={row.code}>
              {fault !== null ? <span data-fault-run-link={row.linked_fault_run?.fault_run_id}>{fault}</span> : null}
              {disconnection !== null ? (
                <span data-disconnection-row-link={row.disconnection_row?.run_id}>{disconnection}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default EarthingLinkPanel;
